// src/components/DebugStepControl/DebugStepControl.jsx
import React, { useState, useCallback } from 'react';
import { FaWrench } from "react-icons/fa6";
import './DebugStepControl.less';

/**
 * Debug Step Control Panel
 * 
 * A development/testing tool that allows manual navigation between steps
 * without having to go through the full application flow.
 * 
 * @param {Object} props
 * @param {number} props.currentStep - The current step number
 * @param {string} props.currentSubStep - The current sub-step (e.g., 'card' or 'receipt' for MAKE_PAYMENT)
 * @param {Object} props.stepMap - Map of step configurations
 * @param {Object} props.steps - Object containing all step constants
 * @param {Function} props.onStepChange - Callback function to change steps
 * @param {Function} props.onSubStepChange - Callback function to change sub-steps
 * @param {boolean} props.debugMode - Whether debug mode is active
 * @param {Function} props.onDebugModeChange - Callback to toggle debug mode
 */
const DebugStepControl = ({ 
  currentStep, 
  currentSubStep,
  stepMap, 
  steps, 
  onStepChange, 
  onSubStepChange,
  debugMode, 
  onDebugModeChange 
}) => {
  const [showPanel, setShowPanel] = useState(false);

  const handleStepClick = useCallback((targetStep) => {
    onStepChange(targetStep);
    // Reset to default sub-step when changing steps
    if (targetStep === steps.MAKE_PAYMENT) {
      onSubStepChange?.('card');
    }
  }, [onStepChange, onSubStepChange, steps.MAKE_PAYMENT]);

  const handleSubStepClick = useCallback((subStep) => {
    onSubStepChange?.(subStep);
  }, [onSubStepChange]);

  const handleDebugModeToggle = useCallback(() => {
    onDebugModeChange(!debugMode);
  }, [debugMode, onDebugModeChange]);

  const getCurrentStepName = () => {
    const stepEntry = Object.entries(steps).find(([, value]) => value === currentStep);
    return stepEntry ? stepEntry[0] : 'Unknown';
  };

  return (
    <>
      {/* Toggle Button */}
      <button
        className="debug-step-control__toggle-btn"
        onClick={() => setShowPanel(!showPanel)}
      >
        {showPanel ? '✕ Close' : <><FaWrench /> Debug</>}
      </button>

      {/* Control Panel */}
      {showPanel && (
        <div className="debug-step-control__panel">
          {/* Header */}
          <h3 className="debug-step-control__header">
            Step Control Panel
          </h3>

          {/* Debug Mode Toggle */}
          <div className="debug-step-control__debug-mode">
            <label className="debug-step-control__debug-mode-label">
              <input
                type="checkbox"
                checked={debugMode}
                onChange={handleDebugModeToggle}
                style={{ marginRight: '8px' }}
              />
              <div className="debug-step-control__label-text">
                <strong>Debug Mode</strong>
                <span style={{ display: 'block', fontSize: '11px', color: '#666' }}>
                  {debugMode ? 'Auto-navigation disabled' : 'Auto-navigation enabled'}
                </span>
              </div>
            </label>
          </div>

          {/* Current Step Info */}
          <div className="debug-step-control__current-step">
            <div className="debug-step-control__current-step-label">
              Current Step:
            </div>
            <div className="debug-step-control__current-step-value">
              {currentStep} - {getCurrentStepName().replace(/_/g, ' ')}
              {currentStep === steps.MAKE_PAYMENT && currentSubStep && (
                <span style={{ marginLeft: '8px', fontSize: '12px', color: '#666' }}>
                  ({currentSubStep})
                </span>
              )}
            </div>
          </div>

          {/* Sub-Step Controls for MAKE_PAYMENT */}
          {currentStep === steps.MAKE_PAYMENT && (
            <div className="debug-step-control__substeps">
              <div className="debug-step-control__substeps-label">
                Payment Sub-Steps:
              </div>
              <div className="debug-step-control__substeps-buttons">
                <button
                  className={`debug-step-control__substep-btn ${
                    currentSubStep === 'card' ? 'debug-step-control__substep-btn--active' : ''
                  }`}
                  onClick={() => handleSubStepClick('card')}
                >
                  Card Authorization
                </button>
                <button
                  className={`debug-step-control__substep-btn ${
                    currentSubStep === 'receipt' ? 'debug-step-control__substep-btn--active' : ''
                  }`}
                  onClick={() => handleSubStepClick('receipt')}
                >
                  Receipt Information
                </button>
              </div>
            </div>
          )}

          {/* Step Buttons */}
          <div className="debug-step-control__steps">
            {Object.entries(steps).map(([name, value]) => {
              const isActive = currentStep === value;

              return (
                <button
                  key={value}
                  className={`debug-step-control__step-btn ${
                    isActive ? 'debug-step-control__step-btn--active' : ''
                  }`}
                  onClick={() => handleStepClick(value)}
                >
                  <div className="debug-step-control__step-content">
                    <span>
                      {value}. {name.replace(/_/g, ' ')}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Footer Info */}
          <div className="debug-step-control__footer">
            Development Tool - Not for Production
          </div>
        </div>
      )}
    </>
  );
};

export default DebugStepControl;
